import { createContext, useContext, useReducer, useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "./AuthContext";

// ─── Helpers ────────────────────────────────────────────────────────────────
export function fmt(n) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency", currency: "GBP", maximumFractionDigits: 0,
  }).format(n);
}
export function fmtShort(n) {
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}£${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}£${(abs / 1_000).toFixed(1)}k`;
  return fmt(n);
}
export function genId() {
  return Math.random().toString(36).slice(2, 10);
}

// ─── Default month ───────────────────────────────────────────────────────────
const defaultMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

// ─── Seed data (used for new users) ──────────────────────────────────────────
const SEED_DATA = {
  currentMonth: defaultMonth(),
  months: {
    [defaultMonth()]: {
      income: [
        { id: "i1", name: "Salary", amount: 4200 },
        { id: "i2", name: "Side Hustle", amount: 300 },
      ],
      expenses: [
        { id: "e1",  name: "Rent",               amount: 1100, category: "needs",   vital: true,  variable: false, dueDate: 1  },
        { id: "e2",  name: "Energy / Utilities",  amount: 120,  category: "needs",   vital: true,  variable: true,  dueDate: 15 },
        { id: "e3",  name: "Groceries",           amount: 280,  category: "needs",   vital: true,  variable: true,  dueDate: 7  },
        { id: "e4",  name: "Transport",           amount: 150,  category: "needs",   vital: true,  variable: false, dueDate: 1  },
        { id: "e5",  name: "Mobile Phone",        amount: 35,   category: "needs",   vital: true,  variable: false, dueDate: 20 },
        { id: "e6",  name: "Home Insurance",      amount: 85,   category: "needs",   vital: true,  variable: false, dueDate: 5  },
        { id: "e7",  name: "Netflix",             amount: 18,   category: "wants",   vital: false, variable: false, dueDate: 10 },
        { id: "e8",  name: "Spotify",             amount: 11,   category: "wants",   vital: false, variable: false, dueDate: 10 },
        { id: "e9",  name: "Gym Membership",      amount: 45,   category: "wants",   vital: false, variable: false, dueDate: 1  },
        { id: "e10", name: "Dining Out",          amount: 180,  category: "wants",   vital: false, variable: true,  dueDate: null },
        { id: "e11", name: "Clothing",            amount: 80,   category: "wants",   vital: false, variable: true,  dueDate: null },
        { id: "e12", name: "Amazon Prime",        amount: 9,    category: "wants",   vital: false, variable: false, dueDate: 12 },
        { id: "e13", name: "Stocks & Shares ISA", amount: 500,  category: "savings", vital: true,  variable: false, dueDate: 28 },
        { id: "e14", name: "Emergency Fund",      amount: 200,  category: "savings", vital: true,  variable: false, dueDate: 28 },
      ],
    },
  },
  assets:      { isa: 15000, pension: 8500, house: 0,    cash: 6200, crypto: 1800, otherAsset: 0      },
  liabilities: { mortgage: 0, studentLoan: 22000, creditCard: 850, otherLiability: 0 },
};

// ─── Reducer ─────────────────────────────────────────────────────────────────
function reducer(state, action) {
  switch (action.type) {
    case "LOAD_STATE":
      return {
        ...action.payload,
        currentMonth: action.payload.currentMonth || defaultMonth(),
      };
    case "RESET_STATE":
      return { ...SEED_DATA, currentMonth: defaultMonth() };

    case "SET_MONTH":
      return { ...state, currentMonth: action.payload };

    case "ADD_INCOME": {
      const m = state.currentMonth;
      const prev = state.months[m] || { income: [], expenses: [] };
      return { ...state, months: { ...state.months, [m]: { ...prev, income: [...prev.income, action.payload] } } };
    }
    case "REMOVE_INCOME": {
      const m = state.currentMonth;
      const prev = state.months[m] || { income: [], expenses: [] };
      return { ...state, months: { ...state.months, [m]: { ...prev, income: prev.income.filter((i) => i.id !== action.payload) } } };
    }
    case "ADD_EXPENSE": {
      const m = state.currentMonth;
      const prev = state.months[m] || { income: [], expenses: [] };
      return { ...state, months: { ...state.months, [m]: { ...prev, expenses: [...prev.expenses, action.payload] } } };
    }
    case "REMOVE_EXPENSE": {
      const m = state.currentMonth;
      const prev = state.months[m] || { income: [], expenses: [] };
      return { ...state, months: { ...state.months, [m]: { ...prev, expenses: prev.expenses.filter((e) => e.id !== action.payload) } } };
    }
    case "UPDATE_EXPENSE": {
      const m = state.currentMonth;
      const prev = state.months[m] || { income: [], expenses: [] };
      return {
        ...state,
        months: {
          ...state.months,
          [m]: { ...prev, expenses: prev.expenses.map((e) => e.id === action.payload.id ? { ...e, ...action.payload.updates } : e) },
        },
      };
    }
    case "UPDATE_ASSETS":
      return { ...state, assets: { ...state.assets, ...action.payload } };
    case "UPDATE_LIABILITIES":
      return { ...state, liabilities: { ...state.liabilities, ...action.payload } };

    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────
const AppContext = createContext(null);

export function AppProvider({ children }) {
  const { user } = useAuth();
  const [state, dispatch] = useReducer(reducer, SEED_DATA);
  const [dataLoading, setDataLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error

  // ── Load user data from Supabase when auth state changes ──────────────────
  useEffect(() => {
    // user is undefined while AuthContext is still checking session
    if (user === undefined) return;

    if (user === null) {
      // Logged out — reset to clean defaults
      dispatch({ type: "RESET_STATE" });
      setDataLoading(false);
      return;
    }

    // Logged in — fetch their saved data
    setDataLoading(true);
    supabase
      .from("user_financial_data")
      .select("data")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!error && data?.data && Object.keys(data.data).length > 0) {
          dispatch({ type: "LOAD_STATE", payload: data.data });
        }
        // If no data found, keep the SEED_DATA already in state (first-time user experience)
        setDataLoading(false);
      });
  }, [user]);

  // ── Debounced save to Supabase whenever state changes ─────────────────────
  useEffect(() => {
    // Don't save while loading initial data or when no user
    if (!user || dataLoading) return;

    setSaveStatus("saving");
    const timer = setTimeout(async () => {
      const { error } = await supabase
        .from("user_financial_data")
        .upsert(
          { user_id: user.id, data: state, updated_at: new Date().toISOString() },
          { onConflict: "user_id" }
        );

      setSaveStatus(error ? "error" : "saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    }, 1200); // 1.2s debounce

    return () => clearTimeout(timer);
  }, [state, user, dataLoading]);

  // ── Derived values ────────────────────────────────────────────────────────
  const monthData = state.months[state.currentMonth] || { income: [], expenses: [] };
  const totalIncome    = monthData.income.reduce((s, i) => s + Number(i.amount), 0);
  const totalNeeds     = monthData.expenses.filter((e) => e.category === "needs").reduce((s, e) => s + Number(e.amount), 0);
  const totalWants     = monthData.expenses.filter((e) => e.category === "wants").reduce((s, e) => s + Number(e.amount), 0);
  const totalSavings   = monthData.expenses.filter((e) => e.category === "savings").reduce((s, e) => s + Number(e.amount), 0);
  const totalExpenses  = totalNeeds + totalWants;
  const leftover       = totalIncome - totalNeeds - totalWants - totalSavings;
  const savingsRate    = totalIncome > 0 ? Math.round(((totalSavings + Math.max(0, leftover)) / totalIncome) * 100) : 0;

  const totalAssets      = Object.values(state.assets).reduce((s, v) => s + Number(v), 0);
  const totalLiabilities = Object.values(state.liabilities).reduce((s, v) => s + Number(v), 0);
  const netWorth         = totalAssets - totalLiabilities;

  return (
    <AppContext.Provider
      value={{
        state, dispatch, dataLoading, saveStatus,
        monthData, totalIncome, totalNeeds, totalWants, totalSavings,
        totalExpenses, leftover, savingsRate,
        totalAssets, totalLiabilities, netWorth,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
